import React, { useEffect, useState, Fragment, memo } from 'react';
import { ArrowRightIcon, ClockIcon, FlameIcon, GiftIcon, HandIcon, HeartIcon, LeafIcon, MapPinIcon, PackageIcon, ShieldCheckIcon, WindIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ColeccionesTabs } from '../components/ColeccionesTabs';
import { useCart } from '../components/CartContext';
type AromaVariantMap = Record<string, number>;
const FRAGMENTOS_VARIANT_IDS: AromaVariantMap = {
  'Tierra · Sándalo + Palo Santo': 44232167293031,
  'Aire · Bergamota + Té verde': 44232167686247,
  'Agua · Acorde marino + Eucalipto': 44232168046695,
  'Hogar · Vainilla + Almendra': 44232168964199,
  'Recuerdo · Manzanilla + Algodón': 44232169881703,
  'Latir · Tulipán + Vainilla suave': 44232170700903,
  'Ritual · Palo Santo + Lavanda': 44232171552871,
  'Descanso · Manzanilla + Lavanda': 44232172175463,
  'Silencio · Algodón + Té negro': 44232172961895
};
const RITUAL_SAVIA_VARIANT_IDS: AromaVariantMap = {
  'Tierra · Sándalo + Palo Santo': 44232175157351,
  'Aire · Bergamota + Té verde': 44232177352807,
  'Agua · Acorde marino + Eucalipto': 44232182300775,
  'Hogar · Vainilla + Almendra': 44232184103015,
  'Recuerdo · Manzanilla + Algodón': 44232185282663,
  'Latir · Tulipán + Vainilla suave': 44232186167399,
  'Ritual · Palo Santo + Lavanda': 44232187674727,
  'Descanso · Manzanilla + Lavanda': 44232188854375,
  'Silencio · Algodón + Té negro': 44232190034023
};
export function Home() {
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('inicio');
  const [activeIntro, setActiveIntro] = useState<'origen' | 'memoria' | 'pausa' | null>(null);
  const [fragmentosAroma, setFragmentosAroma] = useState('');
  const [fragmentosAromaError, setFragmentosAromaError] = useState(false);
  const [ritualAroma, setRitualAroma] = useState('');
  const [ritualAromaError, setRitualAromaError] = useState(false);
  const {
    addItem
  } = useCart();
  const addFragmentos = () => {
    const variantId = FRAGMENTOS_VARIANT_IDS[fragmentosAroma];
    if (!variantId) {
      setFragmentosAromaError(true);
      document.getElementById('fragmentos-aroma')?.focus();
      return;
    }
    addItem({
      variantId,
      name: 'Fragmentos Aromáticos',
      aroma: fragmentosAroma,
      format: 'Pack x10 · 130g aprox.',
      price: 42000,
      image: 'https://cdn.magicpatterns.com/uploads/hfNXz1KRRNk79LtbWbQMLS/FRAGMENTOS.png'
    });
  };
  const addRitualSavia = () => {
    const variantId = RITUAL_SAVIA_VARIANT_IDS[ritualAroma];
    if (!variantId) {
      setRitualAromaError(true);
      document.getElementById('ritual-savia-aroma')?.focus();
      return;
    }
    addItem({
      variantId,
      name: 'Ritual SAVIA',
      aroma: ritualAroma,
      format: 'Pebetero + Pack x10 fragmentos',
      price: 98000,
      image: 'https://cdn.magicpatterns.com/uploads/cmLn7L7Ls22KEHrjLjDVm7/ChatGPT_Image_11_jul_2026,_00_05_12.png'
    });
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });
    const reobserve = () => {
      document.querySelectorAll('.reveal:not(.visible), .reveal-left:not(.visible), .reveal-right:not(.visible)').forEach(el => observer.observe(el));
    };
    reobserve();
    const mo = new MutationObserver(() => reobserve());
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    const onScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const pct = maxScroll > 0 ? scrolled / maxScroll * 100 : 0;
      setProgress(pct);
      const sectionIds = ['inicio', 'colecciones', 'mas-alla', 'nosotros'];
      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight * 0.92) {
        setActiveSection('nosotros');
        return;
      }
      const threshold = window.scrollY + window.innerHeight * 0.35;
      let current = sectionIds[0];
      sectionIds.forEach(id => {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= threshold) current = id;
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => {
      observer.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  const handleSelectorClick = (e: React.MouseEvent, target: 'origen' | 'memoria' | 'pausa') => {
    e.preventDefault();
    setActiveIntro(target);
    setTimeout(() => {
      const intro = document.getElementById(`intro-${target}`);
      const nav = document.getElementById('nav');
      if (intro) {
        // Land on the collection name (skipping the section's top padding
        // and the "Colección 0X" overline) so it sits right under the nav.
        const nombre = intro.querySelector('.coleccion-intro__nombre') as HTMLElement | null;
        const navOffset = (nav?.offsetHeight ?? 0) + 24;
        const top = nombre ? nombre.getBoundingClientRect().top + window.scrollY - navOffset + 6 : intro.offsetTop - navOffset + 6;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
      }
    }, 50);
  };
  return <>
      <div className={`scroll-progress ${progress > 0.5 ? 'active' : ''}`} id="scrollProgress" style={{
      clipPath: `inset(0 ${(100 - progress).toFixed(3)}% 0 0)`,
      '--progress-pct': `${progress}%`
    } as React.CSSProperties} data-id="element-292"></div>

      <nav className="section-nav" id="sectionNav" aria-label="Sección activa" data-id="element-293">
        <div className={`section-nav__item ${activeSection === 'inicio' ? 'active' : ''}`} data-section="inicio" data-id="element-294">
          <span className="section-nav__label" data-id="element-295">Inicio</span>
        </div>
        <div className={`section-nav__item ${activeSection === 'colecciones' ? 'active' : ''}`} data-section="colecciones" data-id="element-296">
          <span className="section-nav__label" data-id="element-297">Colecciones</span>
        </div>

        <div className={`section-nav__item ${activeSection === 'mas-alla' ? 'active' : ''}`} data-section="mas-alla" data-id="element-298">
          <span className="section-nav__label" data-id="element-299">Kits & Objetos</span>
        </div>
        <div className={`section-nav__item ${activeSection === 'nosotros' ? 'active' : ''}`} data-section="nosotros" data-id="element-300">
          <span className="section-nav__label" data-id="element-301">Nosotros</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero" id="inicio" data-id="element-302">
        <div className="hero__visual" data-id="element-303">
          <img src="https://cdn.magicpatterns.com/uploads/3EcUbgfEAQtgcc8aT1UsWJ/ChatGPT_Image_11_jul_2026,_12_56_49.png" alt="Vela SAVIA encendida junto a una ventana con luz natural" style={{
          objectPosition: 'center 40%'
        }} data-id="element-304" />
          <div className="hero__visual-overlay" data-id="element-305"></div>
        </div>
        <div className="hero__content" data-id="element-306">
          <div className="hero__content-inner" data-id="element-307">
            <p className="hero__eyebrow reveal d1" data-id="element-308">
              Bogotá, Colombia · Hecho a mano
            </p>
            <h1 className="hero__title reveal d2" data-id="element-309">
              El aroma que convierte tu espacio en el lugar donde{' '}
              <em data-id="element-310">más quieres estar.</em>
            </h1>
            <div className="hero__line reveal d2" data-id="element-311"></div>
            <p className="hero__subtitle reveal d3" data-id="element-312">
              Bienestar sensorial y ritual en el hogar. Aromas de presencia
              suave, pensados para integrarse al espacio sin invadirlo.
            </p>
            <div className="reveal d3" data-id="element-313">
              <p className="hero__selector-label" data-id="element-314">
                ¿Por dónde quieres empezar?
              </p>
              <div className="hero__selector" data-id="element-315">
                <a href={`#intro-origen`} className="selector-btn" onClick={e => handleSelectorClick(e, 'origen')} data-id="element-316">
                  <LeafIcon size={14} strokeWidth={1.5} data-id="element-317" />
                  Quiero reconectar · Origen
                  <ArrowRightIcon className="selector-btn__arrow" size={12} strokeWidth={1.5} data-id="element-318" />
                </a>
                <a href={`#intro-memoria`} className="selector-btn" onClick={e => handleSelectorClick(e, 'memoria')} data-id="element-319">
                  <HeartIcon size={14} strokeWidth={1.5} data-id="element-320" />
                  Quiero recordar · Memoria
                  <ArrowRightIcon className="selector-btn__arrow" size={12} strokeWidth={1.5} data-id="element-321" />
                </a>
                <a href={`#intro-pausa`} className="selector-btn" onClick={e => handleSelectorClick(e, 'pausa')} data-id="element-322">
                  <FlameIcon size={14} strokeWidth={1.5} data-id="element-323" />
                  Quiero descansar · Pausa
                  <ArrowRightIcon className="selector-btn__arrow" size={12} strokeWidth={1.5} data-id="element-324" />
                </a>
                <Link to="#mas-alla" className="selector-btn" data-id="element-325">
                  <GiftIcon size={14} strokeWidth={1.5} data-id="element-326" />
                  Es un regalo · Kit Ritual
                  <ArrowRightIcon className="selector-btn__arrow" size={12} strokeWidth={1.5} data-id="element-327" />
                </Link>
              </div>
            </div>
            <div className="hero__meta reveal d4" data-id="element-328">
              <span className="hero__meta-item" data-id="element-329">Cera de soya 100% vegetal</span>
              <span className="hero__meta-item" data-id="element-330">·</span>
              <span className="hero__meta-item" data-id="element-331">9 aromas · 3 colecciones</span>
              <span className="hero__meta-item" data-id="element-332">·</span>
              <span className="hero__meta-item" data-id="element-333">Toda Colombia</span>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="manifesto" data-id="element-334">
        <div className="savia-container--narrow" data-id="element-335">
          <div className="manifesto__inner reveal" data-id="element-336">
            <p className="manifesto__overline" data-id="element-337">Bogotá · Hecho a mano · 2025</p>
            <p className="manifesto__quote" data-id="element-338">
              "Aromas que acompañan la forma de habitar."
            </p>
            <div className="manifesto__line" data-id="element-339"></div>
            <p className="manifesto__tagline" data-id="element-340">
              Hecho para habitar en silencio · SAVIA
            </p>
          </div>
        </div>
      </section>

      {/* INTROS DE COLECCIÓN */}
      <section className={`coleccion-intro ${activeIntro === 'origen' ? 'activa' : ''}`} id="intro-origen" data-id="element-341">
        <div className="coleccion-intro__inner" data-id="element-342">
          <div className="coleccion-intro__texto" data-id="element-343">
            <h2 className="coleccion-intro__nombre" data-id="element-344">Origen</h2>
            <p className="coleccion-intro__frase" data-id="element-345">
              Para volver a lo que siempre fuiste.
            </p>
            <p className="coleccion-intro__cuerpo" data-id="element-346">
              Hay momentos en que el mundo exige demasiado y lo único que
              necesitas es recordar que tienes raíces. Origen nació para eso —
              para devolverte al centro, al silencio, a lo esencial. Tres aromas
              que huelen a tierra firme, a aire limpio, a empezar de nuevo.
            </p>
            <div className="coleccion-intro__aromas" data-id="element-347">
              <div className="coleccion-intro__aroma" data-id="element-348">
                <span className="aroma-nombre" data-id="element-349">Tierra</span>
                <span className="aroma-notas" data-id="element-350">Sándalo · Palo Santo</span>
                <span className="aroma-desc" data-id="element-351">
                  Madera antigua, presencia serena. Para cuando necesitas
                  sentirte enraizado.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-352">
                <span className="aroma-nombre" data-id="element-353">Aire</span>
                <span className="aroma-notas" data-id="element-354">Bergamota · Té verde</span>
                <span className="aroma-desc" data-id="element-355">
                  Claridad verde, mente despejada. Para cuando quieres pensar
                  sin ruido.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-356">
                <span className="aroma-nombre" data-id="element-357">Agua</span>
                <span className="aroma-notas" data-id="element-358">Acorde marino · Eucalipto</span>
                <span className="aroma-desc" data-id="element-359">
                  Frescura abierta, respiro limpio. Para soltar lo que ya no
                  necesitas cargar.
                </span>
              </div>
            </div>
          </div>
          <div className="coleccion-intro__visual coleccion-intro__visual--origen" data-id="element-360">
            <img src="https://cdn.magicpatterns.com/uploads/h4xhXLG28mJdfS1A11ueKj/ChatGPT_Image_11_jul_2026,_11_28_12.png" alt="Vela Tierra SAVIA de la colección Origen" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }} data-id="element-361" />
          </div>
        </div>
      </section>

      <section className={`coleccion-intro coleccion-intro--memoria ${activeIntro === 'memoria' ? 'activa' : ''}`} id="intro-memoria" data-id="element-362">
        <div className="coleccion-intro__inner coleccion-intro__inner--reverse" data-id="element-363">
          <div className="coleccion-intro__visual coleccion-intro__visual--memoria" data-id="element-364">
            <img src="https://cdn.magicpatterns.com/uploads/oX7VMHMfxoir8RnTJneG86/ChatGPT_Image_11_jul_2026,_11_40_02.png" alt="Vela Hogar SAVIA de la colección Memoria" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }} data-id="element-365" />
          </div>
          <div className="coleccion-intro__texto" data-id="element-366">
            <h2 className="coleccion-intro__nombre" data-id="element-367">Memoria</h2>
            <p className="coleccion-intro__frase" data-id="element-368">
              Para los momentos que no quieres olvidar.
            </p>
            <p className="coleccion-intro__cuerpo" data-id="element-369">
              El olfato es el único sentido que viaja directamente al recuerdo,
              sin escalas. Memoria nació de esa certeza — de que hay aromas que
              saben a casa, a abrazo, a alguien que amas. Tres fragancias para
              habitar con ternura.
            </p>
            <div className="coleccion-intro__aromas" data-id="element-370">
              <div className="coleccion-intro__aroma" data-id="element-371">
                <span className="aroma-nombre" data-id="element-372">Hogar</span>
                <span className="aroma-notas" data-id="element-373">Vainilla · Almendra</span>
                <span className="aroma-desc" data-id="element-374">
                  Cremoso, dulce, seguro. Huele exactamente como llegar.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-375">
                <span className="aroma-nombre" data-id="element-376">Recuerdo</span>
                <span className="aroma-notas" data-id="element-377">Manzanilla · Algodón</span>
                <span className="aroma-desc" data-id="element-378">
                  Limpio y tierno. Como releer una carta que guardaste mucho
                  tiempo.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-379">
                <span className="aroma-nombre" data-id="element-380">Latir</span>
                <span className="aroma-notas" data-id="element-381">Tulipán · Vainilla suave</span>
                <span className="aroma-desc" data-id="element-382">
                  Floral suave, vivo y presente. Te recuerda que estás aquí.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`coleccion-intro ${activeIntro === 'pausa' ? 'activa' : ''}`} id="intro-pausa" data-id="element-383">
        <div className="coleccion-intro__inner" data-id="element-384">
          <div className="coleccion-intro__texto" data-id="element-385">
            <h2 className="coleccion-intro__nombre" data-id="element-386">Pausa</h2>
            <p className="coleccion-intro__frase" data-id="element-387">
              El arte de no hacer nada — y que eso sea suficiente.
            </p>
            <p className="coleccion-intro__cuerpo" data-id="element-388">
              Vivimos en un mundo que premia la velocidad y castiga el descanso.
              Pausa existe para darte permiso. Para decirte que detenerse no es
              perder el tiempo — es recuperarte. Tres aromas que saben a
              silencio bien habitado.
            </p>
            <div className="coleccion-intro__aromas" data-id="element-389">
              <div className="coleccion-intro__aroma" data-id="element-390">
                <span className="aroma-nombre" data-id="element-391">Ritual</span>
                <span className="aroma-notas" data-id="element-392">Palo Santo · Lavanda</span>
                <span className="aroma-desc" data-id="element-393">
                  Sagrado y sereno. Convierte cualquier rincón en un espacio de
                  intención.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-394">
                <span className="aroma-nombre" data-id="element-395">Descanso</span>
                <span className="aroma-notas" data-id="element-396">Manzanilla · Lavanda</span>
                <span className="aroma-desc" data-id="element-397">
                  Calma herbal, noche bogotana. Permiso de soltar el día.
                </span>
              </div>
              <div className="coleccion-intro__aroma" data-id="element-398">
                <span className="aroma-nombre" data-id="element-399">Silencio</span>
                <span className="aroma-notas" data-id="element-400">Algodón · Té negro</span>
                <span className="aroma-desc" data-id="element-401">
                  No es vacío — es espacio. Para cuando el mundo ha hablado
                  demasiado.
                </span>
              </div>
            </div>
          </div>
          <div className="coleccion-intro__visual coleccion-intro__visual--pausa" data-id="element-402">
            <img src="https://cdn.magicpatterns.com/uploads/eZs29d1Y1UcJyJeaRBLrx9/ChatGPT_Image_11_jul_2026,_11_46_53.png" alt="Vela Silencio SAVIA de la colección Pausa" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }} data-id="element-403" />
          </div>
        </div>
      </section>

      <ColeccionesTabs data-id="element-404" />

      <div className="envio-banner" data-id="element-405">
        <p data-id="element-406">
          Envío gratis en compras desde <strong data-id="element-407">$120.000</strong>
        </p>
      </div>

      {/* TESTIMONIOS */}
      <section className="testimonios" data-id="element-408">
        <div className="testimonios__inner" data-id="element-409">
          <div className="testimonio__item reveal d1" data-id="element-410">
            <p className="testimonio__quote" data-id="element-411">
              Tierra es la vela que siempre había buscado. La uso en mis clases
              de yoga y mis estudiantes siempre preguntan qué es ese aroma.
            </p>
            <span className="testimonio__firma" data-id="element-412">— Carolina V., Cali</span>
          </div>
          <div className="testimonio__item reveal d2" data-id="element-413">
            <p className="testimonio__quote" data-id="element-414">
              Pedí el Ritual SAVIA como regalo y terminé quedándomelo. El
              pebetero y los fragmentos cambiaron completamente el ambiente de
              mi cuarto.
            </p>
            <span className="testimonio__firma" data-id="element-415">— Manuela R., Bogotá</span>
          </div>
          <div className="testimonio__item reveal d3" data-id="element-416">
            <p className="testimonio__quote" data-id="element-417">
              Silencio es exactamente lo que su nombre promete. La enciendo
              cuando llego a casa y algo en mí sabe que ya llegué.
            </p>
            <span className="testimonio__firma" data-id="element-418">— Daniela M., Medellín</span>
          </div>
        </div>
      </section>

      {/* MÁS ALLÁ */}
      <section className="mas-alla" id="mas-alla" data-id="element-419">
        <div className="savia-container" data-id="element-420">
          <div className="mas-alla__header reveal" data-id="element-421">
            <p className="mas-alla__overline" data-id="element-422">Más allá de las velas</p>

            <h2 className="mas-alla__title" data-id="element-423">
              Kits & objetos
              <br data-id="element-424" />
              <em data-id="element-425">para regalar o habitar.</em>
            </h2>
          </div>

          <div className="mas-alla__grid" id="mas-alla-grid" data-id="element-426">
            <div className="product-card reveal d1" data-id="element-427">
              <div className="product-card__image" data-id="element-428">
                <img src="https://cdn.magicpatterns.com/uploads/hfNXz1KRRNk79LtbWbQMLS/FRAGMENTOS.png" alt="Fragmentos Aromáticos SAVIA" data-id="element-429" />
              </div>

              <div className="product-card__info" data-id="element-430">
                <div className="product-card__top" data-id="element-431">
                  <h3 className="product-card__nombre" data-id="element-432">
                    Fragmentos Aromáticos
                  </h3>
                  <p className="product-card__notas" data-id="element-433">Pack x10 · 130g aprox.</p>
                  <div className="aroma-selector" data-id="element-434">
                    <label className="aroma-selector__label" htmlFor="fragmentos-aroma" data-id="element-435">
                      Elige tu aroma
                    </label>
                    <select id="fragmentos-aroma" className={`aroma-selector__select${fragmentosAromaError ? ' aroma-selector__select--error' : ''}`} value={fragmentosAroma} aria-invalid={fragmentosAromaError} onChange={event => {
                    setFragmentosAroma(event.target.value);
                    setFragmentosAromaError(false);
                  }} required data-id="element-436">
                      <option value="" disabled data-id="element-437">
                        — seleccionar —
                      </option>
                      <optgroup label="ORIGEN" data-id="element-438">
                        <option value="Tierra · Sándalo + Palo Santo" data-id="element-439">
                          Tierra · Sándalo + Palo Santo
                        </option>
                        <option value="Aire · Bergamota + Té verde" data-id="element-440">
                          Aire · Bergamota + Té verde
                        </option>
                        <option value="Agua · Acorde marino + Eucalipto" data-id="element-441">
                          Agua · Acorde marino + Eucalipto
                        </option>
                      </optgroup>
                      <optgroup label="MEMORIA" data-id="element-442">
                        <option value="Hogar · Vainilla + Almendra" data-id="element-443">
                          Hogar · Vainilla + Almendra
                        </option>
                        <option value="Recuerdo · Manzanilla + Algodón" data-id="element-444">
                          Recuerdo · Manzanilla + Algodón
                        </option>
                        <option value="Latir · Tulipán + Vainilla suave" data-id="element-445">
                          Latir · Tulipán + Vainilla suave
                        </option>
                      </optgroup>
                      <optgroup label="PAUSA" data-id="element-446">
                        <option value="Ritual · Palo Santo + Lavanda" data-id="element-447">
                          Ritual · Palo Santo + Lavanda
                        </option>
                        <option value="Descanso · Manzanilla + Lavanda" data-id="element-448">
                          Descanso · Manzanilla + Lavanda
                        </option>
                        <option value="Silencio · Algodón + Té negro" data-id="element-449">
                          Silencio · Algodón + Té negro
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="product-card__footer" data-id="element-450">
                  <span className="product-card__precio" data-id="element-451">$42.000</span>
                  <button type="button" className="product-card__cta" onClick={addFragmentos} aria-label="Agregar Fragmentos Aromáticos al carrito" data-id="element-452">
                    Agregar
                    <ArrowRightIcon size={12} strokeWidth={1.5} aria-hidden="true" data-id="element-453" />
                  </button>
                </div>
              </div>
            </div>

            <div className="product-card product-card--coming-soon reveal d2" data-id="element-454">
              <div className="product-card__image" data-id="element-455">
                <div className="product-card__placeholder" data-id="element-456"></div>
                <div className="product-card__coming-soon" aria-hidden="true" data-id="element-457">
                  <span data-id="element-458">Próximamente</span>
                </div>
              </div>
              <div className="product-card__info" data-id="element-459">
                <h3 className="product-card__nombre" data-id="element-460">Presencia I</h3>
                <p className="product-card__notas" data-id="element-461">8 cm · 115g</p>
                <div className="product-card__footer" data-id="element-462">
                  <span className="product-card__precio product-card__precio--soon" data-id="element-463">
                    Disponible pronto
                  </span>
                  <a className="product-card__cta product-card__cta--disabled" aria-disabled="true" tabIndex={-1} onClick={event => event.preventDefault()} data-id="element-464">
                    Pedir
                    <ArrowRightIcon size={12} strokeWidth={1.5} data-id="element-465" />
                  </a>
                </div>
              </div>
            </div>

            <div className="product-card product-card--coming-soon reveal d3" data-id="element-466">
              <div className="product-card__image" data-id="element-467">
                <div className="product-card__placeholder" data-id="element-468"></div>
                <div className="product-card__coming-soon" aria-hidden="true" data-id="element-469">
                  <span data-id="element-470">Próximamente</span>
                </div>
              </div>
              <div className="product-card__info" data-id="element-471">
                <h3 className="product-card__nombre" data-id="element-472">Presencia II</h3>
                <p className="product-card__notas" data-id="element-473">12 cm · 115g</p>
                <div className="product-card__footer" data-id="element-474">
                  <span className="product-card__precio product-card__precio--soon" data-id="element-475">
                    Disponible pronto
                  </span>
                  <a className="product-card__cta product-card__cta--disabled" aria-disabled="true" tabIndex={-1} onClick={event => event.preventDefault()} data-id="element-476">
                    Pedir
                    <ArrowRightIcon size={12} strokeWidth={1.5} data-id="element-477" />
                  </a>
                </div>
              </div>
            </div>

            <div className="product-card reveal d4" data-id="element-478">
              <div className="product-card__image" data-id="element-479">
                <img src="https://cdn.magicpatterns.com/uploads/cmLn7L7Ls22KEHrjLjDVm7/ChatGPT_Image_11_jul_2026,_00_05_12.png" alt="Ritual SAVIA con pebetero y fragmentos aromáticos" data-id="element-480" />
              </div>
              <div className="product-card__info" data-id="element-481">
                <div className="product-card__top" data-id="element-482">
                  <h3 className="product-card__nombre" data-id="element-483">Ritual SAVIA</h3>
                  <p className="product-card__notas" data-id="element-484">
                    Pebetero + Pack x10 fragmentos · Aroma a elección
                  </p>
                  <div className="aroma-selector" data-id="element-485">
                    <label className="aroma-selector__label" htmlFor="ritual-savia-aroma" data-id="element-486">
                      Elige tu aroma
                    </label>
                    <select id="ritual-savia-aroma" className={`aroma-selector__select${ritualAromaError ? ' aroma-selector__select--error' : ''}`} value={ritualAroma} aria-invalid={ritualAromaError} onChange={event => {
                    setRitualAroma(event.target.value);
                    setRitualAromaError(false);
                  }} required data-id="element-487">
                      <option value="" disabled data-id="element-488">
                        — seleccionar —
                      </option>
                      <optgroup label="ORIGEN" data-id="element-489">
                        <option value="Tierra · Sándalo + Palo Santo" data-id="element-490">
                          Tierra · Sándalo + Palo Santo
                        </option>
                        <option value="Aire · Bergamota + Té verde" data-id="element-491">
                          Aire · Bergamota + Té verde
                        </option>
                        <option value="Agua · Acorde marino + Eucalipto" data-id="element-492">
                          Agua · Acorde marino + Eucalipto
                        </option>
                      </optgroup>
                      <optgroup label="MEMORIA" data-id="element-493">
                        <option value="Hogar · Vainilla + Almendra" data-id="element-494">
                          Hogar · Vainilla + Almendra
                        </option>
                        <option value="Recuerdo · Manzanilla + Algodón" data-id="element-495">
                          Recuerdo · Manzanilla + Algodón
                        </option>
                        <option value="Latir · Tulipán + Vainilla suave" data-id="element-496">
                          Latir · Tulipán + Vainilla suave
                        </option>
                      </optgroup>
                      <optgroup label="PAUSA" data-id="element-497">
                        <option value="Ritual · Palo Santo + Lavanda" data-id="element-498">
                          Ritual · Palo Santo + Lavanda
                        </option>
                        <option value="Descanso · Manzanilla + Lavanda" data-id="element-499">
                          Descanso · Manzanilla + Lavanda
                        </option>
                        <option value="Silencio · Algodón + Té negro" data-id="element-500">
                          Silencio · Algodón + Té negro
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div className="product-card__footer" data-id="element-501">
                  <span className="product-card__precio" data-id="element-502">$98.000</span>
                  <button type="button" className="product-card__cta" onClick={addRitualSavia} aria-label="Agregar Ritual SAVIA al carrito" data-id="element-503">
                    Agregar
                    <ArrowRightIcon size={12} strokeWidth={1.5} aria-hidden="true" data-id="element-504" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOSOTROS */}
      <section className="nosotros" id="nosotros" data-id="element-505">
        <div className="savia-container" data-id="element-506">
          <div className="nosotros__grid" data-id="element-507">
            <div className="nosotros__visual reveal-left" data-id="element-508">
              <img src="https://cdn.magicpatterns.com/uploads/vvTWywxkYpg18yqapKCtsi/ChatGPT_Image_11_jun_2026,_10_56_01.png" alt="Juan y Alejandra elaborando velas y fragmentos SAVIA a mano" data-id="element-509" />
            </div>

            <div className="reveal-right" data-id="element-510">
              <p className="nosotros__overline" data-id="element-511">La historia detrás</p>
              <h2 className="nosotros__title" data-id="element-512">
                SAVIA nació de
                <br data-id="element-513" />
                <em data-id="element-514">un reencuentro.</em>
              </h2>
              <p className="nosotros__texto" data-id="element-515">
                Juan y Alejandra se reencontraron y nació el deseo de habitar
                juntos. En ese trayecto se dieron cuenta de algo que parece
                simple pero cambia todo: los espacios necesitan sentirse de una
                manera más agradable, más propia, más presente.
              </p>
              <p className="nosotros__texto" data-id="element-516">
                El aroma fue la respuesta. No porque sea decoración, sino porque
                es el único lenguaje que va directo al cuerpo — sin pasar por la
                mente, sin pedir permiso.
              </p>
              <p className="nosotros__texto" data-id="element-517">
                Así nació SAVIA. Dos personas. Un estudio en Bogotá. Cera de
                soya que se vierte a mano. Y la convicción de que la forma en
                que huele tu casa determina cómo te sientes en ella.
              </p>

              <p className="nosotros__firma" data-id="element-518">
                — Juan & Alejandra · Bogotá, Colombia
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PILARES + ENVÍOS */}
      <section className="pilares" data-id="element-519">
        <div className="savia-container--wide" data-id="element-520">
          <div className="pilares__grid" data-id="element-521">
            <div className="pilar reveal d1" data-id="element-522">
              <div className="pilar__num" data-id="element-523">01</div>
              <div className="pilar__icon" data-id="element-524">
                <LeafIcon size={18} strokeWidth={1.2} data-id="element-525" />
              </div>
              <h3 className="pilar__titulo" data-id="element-526">Cera de soya 100% vegetal</h3>
              <p className="pilar__texto" data-id="element-527">
                Sin parafina. Sin derivados del petróleo. Sin tóxicos. Lo que
                entra al aire que respiras importa.
              </p>
            </div>
            <div className="pilar reveal d2" data-id="element-528">
              <div className="pilar__num" data-id="element-529">02</div>
              <div className="pilar__icon" data-id="element-530">
                <HandIcon size={18} strokeWidth={1.2} data-id="element-531" />
              </div>
              <h3 className="pilar__titulo" data-id="element-532">Hecha a mano en Bogotá</h3>
              <p className="pilar__texto" data-id="element-533">
                Cada vela vertida y terminada a mano. Sin producción industrial.
                Con presencia real en cada pieza.
              </p>
            </div>
            <div className="pilar reveal d3" data-id="element-534">
              <div className="pilar__num" data-id="element-535">03</div>
              <div className="pilar__icon" data-id="element-536">
                <WindIcon size={18} strokeWidth={1.2} data-id="element-537" />
              </div>
              <h3 className="pilar__titulo" data-id="element-538">Aromas con notas reales</h3>
              <p className="pilar__texto" data-id="element-539">
                Sándalo, palo santo, bergamota, tulipán, lavanda. Fragancias
                construidas desde la emoción.
              </p>
            </div>
            <div className="pilar reveal d4" data-id="element-540">
              <div className="pilar__num" data-id="element-541">04</div>
              <div className="pilar__icon" data-id="element-542">
                <PackageIcon size={18} strokeWidth={1.2} data-id="element-543" />
              </div>
              <h3 className="pilar__titulo" data-id="element-544">Packaging consciente</h3>
              <p className="pilar__texto" data-id="element-545">
                Kraft natural, papel mantequilla, pita de algodón. Todo
                reciclable. El cuidado no termina en la vela.
              </p>
            </div>
          </div>
        </div>

        <div className="envios-strip" data-id="element-546">
          <div className="envio-item reveal d1" data-id="element-547">
            <div className="envio-item__icon" data-id="element-548">
              <ClockIcon size={20} strokeWidth={1.2} data-id="element-549" />
            </div>
            <div data-id="element-550">
              <p className="envio-item__titulo" data-id="element-551">Producción bajo pedido</p>
              <p className="envio-item__texto" data-id="element-552">
                Cada vela se hace especialmente para ti. Despacho en 2–4 días
                hábiles desde Bogotá.
              </p>
            </div>
          </div>
          <div className="envio-item reveal d2" data-id="element-553">
            <div className="envio-item__icon" data-id="element-554">
              <MapPinIcon size={20} strokeWidth={1.2} data-id="element-555" />
            </div>
            <div data-id="element-556">
              <p className="envio-item__titulo" data-id="element-557">Envíos a toda Colombia</p>
              <p className="envio-item__texto" data-id="element-558">
                Bogotá, Medellín, Cali, Barranquilla y todo el territorio.
                Empaque protegido.
              </p>
            </div>
          </div>
          <div className="envio-item reveal d3" data-id="element-559">
            <div className="envio-item__icon" data-id="element-560">
              <ShieldCheckIcon size={20} strokeWidth={1.2} data-id="element-561" />
            </div>
            <div data-id="element-562">
              <p className="envio-item__titulo" data-id="element-563">Pago seguro</p>
              <p className="envio-item__texto" data-id="element-564">
                MercadoPago · PSE · Contraentrega disponible en Colombia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="cta-final" id="contacto" data-id="element-565">
        <div className="savia-container--narrow" data-id="element-566">
          <p className="cta-final__overline reveal" data-id="element-567">Una sola decisión</p>
          <h2 className="cta-final__title reveal d1" data-id="element-568">
            Llevas años construyendo el espacio perfecto. Ya es hora de que ese
            espacio <em data-id="element-569">también te cuide a ti.</em>
          </h2>
          <p className="cta-final__sub reveal d2" data-id="element-570">
            No necesitas una casa más grande. Solo necesitas un momento, una
            llama y una intención.
          </p>
          <div className="cta-final__botones reveal d3" data-id="element-571">
            <Link to="#colecciones" className="btn-primary" data-id="element-572">
              <ArrowRightIcon size={14} strokeWidth={1.5} data-id="element-573" />
              Ver colecciones
            </Link>
            <a href="#mas-alla" className="btn-outline" onClick={e => {
            e.preventDefault();
            const target = document.getElementById('mas-alla');
            const nav = document.getElementById('nav');
            if (target) {
              const offset = (nav?.offsetHeight ?? 0) + 24;
              const top = target.getBoundingClientRect().top + window.scrollY - offset + 120;
              window.scrollTo({
                top,
                behavior: 'smooth'
              });
            }
          }} data-id="element-574">
              Ritual SAVIA
            </a>
          </div>
          <div className="cta-final__divider reveal d4" data-id="element-575"></div>
          <p className="cta-final__instagram reveal d4" data-id="element-576">
            ¿Tienes dudas? Escríbenos:{' '}
            <a href="https://wa.me/573213396782?text=Hola%20SAVIA" target="_blank" rel="noopener noreferrer" data-id="element-577">
              WhatsApp
            </a>{' '}
            · Instagram:{' '}
            <a href="https://instagram.com/savia.ritualco" target="_blank" rel="noopener noreferrer" data-id="element-578">
              @savia.ritualco
            </a>
          </p>
        </div>
      </section>
    </>;
}